// -----------------------------------------------------------------Imports---
import { Button, Grid, Paper, TextField, Typography } from "@mui/material";
import { Divider } from "@mui/material";
import { Key, useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";

import { gridKeys, gridKeys2, PartnerGridKey, PartnerModel } from "../../models/PartnerModel";
import ResponseModel from "../../models/ResponseModel";
import PartnerService from "../../services/partners/PartnerService";
import { getLongAddress, hasApiError } from "../../utils/Utils";

import { style } from "./AddPartner.style";

type AddPartnerProps = {
  id: number | undefined;
  setSetterOpen: React.Dispatch<React.SetStateAction<boolean>>;
  getPartners: () => Promise<void>;
};

// -----------------------------------------------------------------Exports---
const AddPartner = (props: AddPartnerProps): JSX.Element => {
  const [partner, setPartner] = useState<PartnerModel>({} as PartnerModel);
  const [id] = useState<number | undefined>(props.id);

  //get a partner to update, if id is available
  const getPartner = useCallback(async (partnerId: number): Promise<void> => {
    const response: ResponseModel = await PartnerService.getPartner(partnerId.toString());

    if (hasApiError(response)) return;
    else {
      const data: PartnerModel = response.data as PartnerModel;
      setPartner({
        ...data,
        //filter the +36 and 06 out
        telephone: data.telephone?.startsWith("+36")
          ? data.telephone.substring(3, data.telephone.length).toString()
          : data.telephone?.startsWith("06")
            ? data.telephone.substring(2, data.telephone.length).toString()
            : data.telephone?.toString(),
      } as PartnerModel);
    }
  }, []);

  //saving the new partner
  const savePartner = async (): Promise<void> => {
    //guards
    if (partner.name === undefined || partner.name.length < 3) {
      toast.error("A név legalább 3 karakter kell legyen");
      return;
    }
    if (partner.taxNumber === undefined) {
      toast.error("Az adószámot meg kell adni");
      return;
    }
    if (!partner.representative) {
      toast.error("Képviselőt meg kell adni");
      return;
    }
    if (!partner.telephone) {
      toast.error("Telefon számot meg kell adni");
      return;
    }
    if (!partner.email || partner.email.length < 6) {
      toast.error("Az email legalább 6 karakter kell legyen");
      return;
    }
    if (!partner.postalCode) {
      toast.error("Irányítószámot meg kell adni");
      return;
    }
    if (!partner.city) {
      toast.error("Várost meg kell adni");
      return;
    }
    if (!partner.streetName) {
      toast.error("Közterület nevét meg kell adni");
      return;
    }
    if (!partner.streetType) {
      toast.error("Közterület típusát meg kell adni");
      return;
    }
    if (!partner.houseNumber) {
      toast.error("Házszámot meg kell adni");
      return;
    }
    if (!partner.notificationAddress) {
      toast.error("Értesítési cím nem került kitöltésre");
      return;
    }
    if (partner.representative.length <= 3) {
      toast.error("A képviselő hossza nagyobb kell legyen mint 3");
      return;
    }
    if (partner.telephone.length <= 3) {
      toast.error("A telefon hossza nagyobb kell legyen mint 3");
      return;
    }
    if (partner.email.length <= 3) {
      toast.error("Az email hossza nagyobb kell legyen mint 3");
      return;
    }
    if (partner.postalCode.length <= 3) {
      toast.error("Az irányítószám hossza nagyobb kell legyen mint 3");
      return;
    }
    if (partner.city.length <= 0) {
      toast.error("A városnak meg kell adni valamit");
      return;
    }
    if (partner.streetName.length <= 0) {
      toast.error("A közterület neve hossza nagyobb kell legyen mint 0");
      return;
    }
    if (partner.streetType.length <= 0) {
      toast.error("A közterület hossza nagyobb kell legyen mint 0");
      return;
    }
    if (partner.houseNumber.length <= 0) {
      toast.error("A házszám hossza nagyobb kell legyen mint 0");
      return;
    }
    if (partner.notificationAddress.length <= 8) {
      toast.error("Értesítési cím hossza nagyobb kell legyen mint 9");
      return;
    }
    const emailRegex: RegExp = /^([A-Za-z0-9_\-.])+@([A-Za-z0-9_\-.])+\.([A-Za-z]{2,4})$/;
    if (!emailRegex.test(partner.email)) {
      toast.error("Nem megfelelő az email formátum");
      return;
    }
    const VATRegex: RegExp = /^(\d{7})(\d)-([1-5])-(0[2-9]|[13][0-9]|2[02-9]|4[0-4]|51)$/;
    const matches: RegExpMatchArray | null = partner.taxNumber.match(VATRegex);
    if (!matches) {
      toast.error("Nem megfelelő az adószám formátuma (12345678-0-00)");
      return;
    } else {
      //magic hun taxnumber validator
      const mul: number[] = [9, 7, 3, 1, 9, 7, 3];
      const base: string[] = matches[1].toString().split("");
      const check: number = parseInt(matches[2].toString());
      let sum: number = 0;
      for (let i: number = 0; i < 7; i++) {
        sum += parseInt(base[i]) * mul[i];
      }
      let last: number = sum % 10;
      if (last > 0) {
        last = 10 - last;
      }
      if (last !== check) {
        toast.error("Nem létező adószámot adott meg");
        return;
      }
    }

    //sending the request
    const loadingToastId = toast.loading("Mentés folyamatban...");
    let response: ResponseModel;
    if (id === undefined) response = await PartnerService.addPartner(partner as Record<string, unknown>);
    else response = await PartnerService.updatePartner(partner as Record<string, unknown>, id.toString());

    //showing the result
    if (hasApiError(response)) {
      toast.dismiss(loadingToastId);
      toast.error(response.error?.fields?.[0]?.message);
    } else {
      const msg = id === undefined ? "Sikeres partner rögzítés" : "Sikeres partner frissítés";
      toast.update(loadingToastId, { render: msg, type: "success", isLoading: false, autoClose: 4000 });
      props.setSetterOpen(false);
      void props.getPartners();
    }
  };

  //updating the new partner state
  const onTextChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, key: string): void => {
    const newPartner: PartnerModel = { ...partner };
    (newPartner as Record<string, unknown>)[key] = e.target.value;
    setPartner(newPartner);
  };

  const loadDefaultNotificationAddress = (): void => {
    const newPartner: PartnerModel = { ...partner };
    newPartner.notificationAddress = getLongAddress(newPartner);
    setPartner(newPartner);
  };

  //devnote: somehow this is enough
  useEffect((): void => {
    if (id !== undefined) {
      void getPartner(id);
    }
  }, [id, getPartner]);

  // --------------------------------------------------------------Return---
  return (
    <Paper style={style.paper}>
      <Typography variant="h3" style={style.title}>
        {id === undefined && "Ügyfél hozzáadása"}
        {id !== undefined && "Ügyfél frissítése"}
      </Typography>

      {/* Company data */}
      <Divider style={style.divider} textAlign="left">
        Céges adatok
      </Divider>
      <Grid container spacing={4}>
        {gridKeys
          .filter((gridKey: PartnerGridKey): boolean => gridKey.inputHidden !== true)
          .map((gridKey: PartnerGridKey, i: Key): JSX.Element => {
            return (
              <Grid item key={i} md={gridKey.md ?? 3} sm={gridKey.sm ?? 6} xs={gridKey.xs ?? 12}>
                <Typography style={style.gridLabel} align={gridKey.align ?? "left"}>
                  {gridKey.label}
                </Typography>
                {!gridKey.customInput && (
                  <TextField
                    fullWidth
                    value={(partner as Record<string, unknown>)[gridKey.key] ?? ""}
                    variant="standard"
                    onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void =>
                      onTextChange(e, gridKey.key)
                    }
                    InputProps={gridKey.InputProps}
                    inputProps={gridKey.inputProps}
                  />
                )}
                {gridKey.customInput && (
                  <gridKey.customInput
                    value={((partner as Record<string, unknown>)[gridKey.key] ?? "") as string}
                    onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void =>
                      onTextChange(e, gridKey.key)
                    }
                  />
                )}
                {gridKey.mandatory && <Typography style={style.helper}>Kötelező mező*</Typography>}
              </Grid>
            );
          })}
      </Grid>

      {/* LocationData */}
      <Divider style={style.divider} textAlign="left">
        Cím adatok
      </Divider>
      <Grid container spacing={4}>
        {gridKeys2
          .filter((gridKey: PartnerGridKey): boolean => gridKey.inputHidden !== true)
          .map((gridKey: PartnerGridKey, i: Key): JSX.Element => {
            return (
              <Grid item key={i} md={gridKey.md ?? 3} sm={gridKey.sm ?? 6} xs={gridKey.xs ?? 12}>
                <Typography style={style.gridLabel} align={gridKey.align ?? "left"}>
                  {gridKey.label}
                </Typography>
                <TextField
                  fullWidth
                  value={(partner as Record<string, unknown>)[gridKey.key] ?? ""}
                  variant="standard"
                  onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void =>
                    onTextChange(e, gridKey.key)
                  }
                />
                {gridKey.mandatory && <Typography style={style.helper}>Kötelező mező*</Typography>}
              </Grid>
            );
          })}
      </Grid>

      {/* Footer buttons */}
      <Button color="success" onClick={loadDefaultNotificationAddress} variant="contained" style={style.saveButton}>
        Székhely címének másolása
      </Button>
      <Button
        onClick={(): void => {
          void savePartner();
        }}
        variant="contained"
        style={style.saveButton}
      >
        {id === undefined && "Rögzítés"}
        {id !== undefined && "Frissítés"}
      </Button>
      <Button
        color="error"
        onClick={(): void => props.setSetterOpen(false)}
        variant="contained"
        style={style.saveButton}
      >
        Mégse
      </Button>
    </Paper>
  );
};

export default AddPartner;
